import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function LocalInfra() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "Setting Up Production-Like Local Infra on Ubuntu 26.04",
        "July 16, 2026",
        "6min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p className="lead">
                The workspace I develop TiiRADA on is a plain Ubuntu 26.04 LTS desktop. But the
                infrastructure that runs behind it is anything but plain: a production-grade stack
                that lives entirely on my machine. This is how I set it up.
            </p>

            <h2 className="post-sect">Why mirror production locally</h2>
            <p>
                When your production app runs on PostgreSQL and object storage, the worst thing you
                can do is develop against a completely different engine. Queries, storage APIs, and
                background jobs all behave differently across databases. The idea is simple: if the
                local environment uses the same database engine and the same S3-compatible storage
                backend as production, then what works locally ships with confidence.
            </p>
            <p>
                TiiRADA runs on Fly.io Postgres and Cloudflare R2 in production. Locally, that maps
                to a native PostgreSQL cluster and a MinIO container — the same engines, the same
                APIs.
            </p>

            <h2 className="post-sect">PostgreSQL, natively</h2>
            <p>
                Postgres runs natively on the machine rather than in Docker. There is a reason for
                this: it is the engine I trust for the data that matters, and running it as a system
                service means it starts on boot and behaves like the long-lived database it is.
            </p>
            <pre>
                <code className="language-bash">{`# Start the PostgreSQL cluster
sudo systemctl start postgresql

# Check the cluster (PG 18 on Ubuntu 26.04)
pg_lsclusters`}</code>
            </pre>
            <p>
                The setup script parses the database URL with Ruby to pull out host, port, user, and
                database name, checks that Postgres is accepting connections with{" "}
                <code>pg_isready</code>, and then creates the database only if it is missing:
            </p>
            <pre>
                <code className="language-bash">{`export LOCAL_DATABASE_URL=postgresql://postgres@localhost:5432/tiirada_be_development

# scripts/local/setup_local_postgres.sh
# - parses the URL (Ruby)
# - does NOT run if pg_isready fails
# - creates the DB only if it does not exist yet`}</code>
            </pre>
            <p>
                Isolation is built in at the database level. The dev environment and the AI-agent
                test harness each get their own database —{" "}
                <code>tiirada_be_development</code> and <code>tiirada_be_agent_test</code> — so the
                agent can chew on sample data without ever touching your real local records.
            </p>

            <h2 className="post-sect">Object storage with MinIO (Docker Compose)</h2>
            <p>
                MinIO is S3-compatible, which means it speaks the exact same API as the Cloudflare
                R2 used in production. Uploading a photo, an attachment, or a portfolio image to a
                bucket works identically whether it lands on MinIO locally or R2 in production.
            </p>
            <p>
                The whole object-storage layer is defined in a single Docker Compose file. When you
                take down and rebuild the environment, the data survives because it lives on a named
                volume:
            </p>
            <pre>
                <code className="language-yaml">{`services:
  minio:
    image: minio/minio:latest
    container_name: tiirada-minio
    ports:
      - "9000:9000"
      - "9001:9001"
    environment:
      MINIO_ROOT_USER: \${MINIO_ACCESS_KEY:-minioadmin}
      MINIO_ROOT_PASSWORD: \${MINIO_SECRET_KEY:-minioadmin}
    volumes:
      - minio_data:/data
    command: server /data --console-address ":9001"
    healthcheck:
      test: ["CMD", "curl", "-f", "http://localhost:9000/minio/health/live"]
      interval: 10s
      timeout: 5s
      retries: 3
volumes:
  minio_data:`}</code>
            </pre>
            <p>
                Port <code>9000</code> is the S3 API. Port <code>9001</code> is the web console.
                The <code>MINIO_ROOT_USER</code> and <code>MINIO_ROOT_PASSWORD</code> come from the
                project&apos;s <code>.env</code> file, falling back to <code>minioadmin</code> in
                development.
            </p>

            <h3 className="post-sect-sub">Waiting for the API, then creating buckets</h3>
            <p>
                After <code>docker compose up -d</code>, the setup script polls the MinIO health
                endpoint before doing anything else. It runs the health check from a throwaway
                container on the host network:
            </p>
            <pre>
                <code className="language-bash">{`# Wait for MinIO to become ready (max 30s)
for attempt in {1..30}; do
  if docker run --rm --network host curlimages/curl:8.10.1 \\
     -fsS "http://127.0.0.1:9000/minio/health/live" >/dev/null 2>&1; then
    break
  fi
  if [[ "$attempt" -eq 30 ]]; then
    echo "MinIO did not become ready"
    exit 1
  fi
  sleep 1
done`}</code>
            </pre>
            <p>Once MinIO is up, the bucket is created with the MinIO client, ignoring the error if it already exists:</p>
            <pre>
                <code className="language-bash">{`docker run --rm --network host --entrypoint sh minio/mc -c "
  mc alias set local 'http://127.0.0.1:9000' '$MINIO_ACCESS_KEY' '$MINIO_SECRET_KEY' >/dev/null &&
  mc mb --ignore-existing 'local/$MINIO_BUCKET'"
`}</code>
            </pre>
            <p>
                The script runs once for the dev environment and once for the agent environment, so
                two isolated buckets come up: <code>tiirada-dev</code> and{" "}
                <code>tiirada-agent</code>.
            </p>

            <h2 className="post-sect">One command to boot it all</h2>
            <p>
                Everything above is orchestrated by a single script. This is the entrypoint I run
                after a fresh clone or a reboot of the workstation:
            </p>
            <pre>
                <code className="language-bash">{`bin/setup-local-infra`}</code>
            </pre>
            <p>It does, in order:</p>
            <pre>
                <code className="language-bash">{`#!/usr/bin/env bash
set -euo pipefail

APP_ROOT="$(cd "$(dirname "\${BASH_SOURCE[0]}")/.." && pwd)"

echo "Starting local infrastructure..."

# 1. Start PostgreSQL (systemd)
sudo systemctl start postgresql || echo "PostgreSQL may already be running"

# 2. Start MinIO + create buckets, once per environment file
if [[ -f "$APP_ROOT/.env" ]]; then
  ENV_FILE="$APP_ROOT/.env" "$APP_ROOT/scripts/local/setup_local_minio.sh"
else
  "$APP_ROOT/scripts/local/setup_local_minio.sh"
fi

if [[ -f "$APP_ROOT/.env.agent" ]]; then
  ENV_FILE="$APP_ROOT/.env.agent" "$APP_ROOT/scripts/local/setup_local_minio.sh"
fi`}</code>
            </pre>
            <p>
                When it finishes, it prints a summary so you know exactly what is ready and where:
                Postgres on <code>5432</code>, MinIO API on <code>9000</code>, the console on{" "}
                <code>9001</code>, each database, and each bucket.
            </p>

            <h2 className="post-sect">Background jobs: Solid Queue</h2>
            <p>
                In production, TiiRADA uses Solid Queue to process background jobs from the
                database. Development honors the same pattern, so the Rails server and the Solid
                Queue worker run side by side. The worker comes up with a single command:
            </p>
            <pre>
                <code className="language-bash">{`# Run the Solid Queue worker alongside the Rails server
bin/jobs`}</code>
            </pre>
            <p>
                Running the real job processor locally means if a job misbehaves on your machine, it
                will misbehave the same way in production — and you catch it before it ever ships.
            </p>

            <h2 className="post-sect">Working with production-like data</h2>
            <p>
                Infrastructure is only half the story. To make the local environment genuinely
                production-like, there are scripts to restore a snapshot of production data into the
                local database, then sanitize it so no real user data leaks into your dev machine:
            </p>
            <pre>
                <code className="language-bash">{`# Pull a production snapshot down and restore it locally
scripts/local/restore_production_snapshot_to_local.sh

# Sanitize the restored snapshot (strip real user data)
scripts/local/sanitize_local_snapshot.rb

# Seed the agent test environment
scripts/local/seed_maestro_agent_data.rb`}</code>
            </pre>
            <p>
                Developing against realistic data changes everything. Pagination, indexes, and query
                performance all behave like they do in production, instead of surprising you on
                deploy day.
            </p>

            <h2 className="post-sect">The environment at a glance</h2>
            <ul className="lead">
                <li><strong>PostgreSQL 18</strong> — native systemd service, dev + agent databases</li>
                <li><strong>MinIO</strong> — Docker Compose, S3 API on 9000, console on 9001</li>
                <li><strong>Docker Compose</strong> — orchestrates MinIO via <code>docker-compose.infra.yml</code></li>
                <li><strong>Solid Queue worker</strong> — <code>bin/jobs</code>, mirrors production job processing</li>
                <li><strong>Ubuntu 26.04 LTS</strong> — the whole environment on one workhorse of a desktop</li>
            </ul>

            <h2 className="post-sect">Summary</h2>
            <p>
                A production-like local environment is less about tools and more about discipline:
                use the same database engine, the same storage API, and the same async job runner in
                development as you do in production. A single <code>bin/setup-local-infra</code>{" "}
                brings it all up, and production-like snapshots keep the data honest. Set up once,
                and every feature you build locally is a feature you can trust to ship.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}